/** A parent setting together with the dependent rows that sit beneath it. */
export interface ISettingDependencyGroup<T> {
	parent: T;
	dependents: T[];
}

interface IDependent {
	isSubRow?: boolean;
	/**
	 * Dependency never crosses a group boundary. Rows arrive as one flat list spanning every
	 * category, so without this the nearest preceding parent could belong to another group — and
	 * the rows built last, such as the custom dashboard cards, would surface under groups that do
	 * not own them.
	 */
	category?: unknown;
}

interface INamedDependent extends IDependent {
	labelText?: string;
}

/**
 * Fold the flat, render-ordered rows into parent/dependent groups.
 *
 * Dependency is positional: a row marked `isSubRow` belongs to the nearest preceding row that
 * is not, which is the same relationship the old per-row indent expressed. A leading sub-row
 * with no parent becomes its own group rather than being dropped.
 */
export function groupByDependency<T extends IDependent>(entries: T[]): ISettingDependencyGroup<T>[] {
	const groups: ISettingDependencyGroup<T>[] = [];

	for (const entry of entries) {
		const currentGroup = groups[groups.length - 1];
		if (entry.isSubRow && currentGroup && currentGroup.parent.category === entry.category) {
			currentGroup.dependents.push(entry);
			continue;
		}
		groups.push({ parent: entry, dependents: [] });
	}

	return groups;
}

/**
 * Each dependent row mapped to the parent row it sits under.
 *
 * Returns the parent itself rather than just its label, so callers can also read its state — a
 * dependent's presentation depends on whether its parent is on.
 *
 * Built from the unfiltered list on purpose: search results only contain the rows that matched, and
 * a dependent's parent often is not among them, so the relationship has to be resolved before
 * filtering or it is lost.
 */
export function mapDependentsToParents<T extends INamedDependent>(entries: T[]): Map<T, T | undefined> {
	const parents = new Map<T, T | undefined>();
	let currentParent: T | undefined;
	let currentCategory: unknown;

	for (const entry of entries) {
		if (entry.category !== currentCategory) {
			currentCategory = entry.category;
			currentParent = undefined;
		}
		if (entry.isSubRow) {
			parents.set(entry, currentParent);
			continue;
		}
		currentParent = entry;
	}

	return parents;
}

export interface ISettingResultChunk<T> {
	parent: T;
	/** True when the parent matched in its own right, rather than being shown for context. */
	parentMatched: boolean;
	dependents: T[];
}

/**
 * Fold a flat list of matches into parent-led chunks, pulling in each matched dependent's parent
 * even when the parent did not match, so a result is never shown without the setting that governs
 * it.
 *
 * A parent appears once per chunk however many of its dependents matched, and is not repeated when
 * it matched in its own right. Order follows first appearance in the matches.
 */
export function toResultChunks<T extends INamedDependent>(
	matches: T[],
	parents: Map<T, T | undefined>
): ISettingResultChunk<T>[] {
	const chunks = new Map<T, ISettingResultChunk<T>>();
	const matchSet = new Set(matches);

	for (const match of matches) {
		// An orphan dependent, with no parent to show, leads its own chunk.
		const owner = match.isSubRow ? (parents.get(match) ?? match) : match;

		let chunk = chunks.get(owner);
		if (!chunk) {
			chunk = { parent: owner, parentMatched: matchSet.has(owner), dependents: [] };
			chunks.set(owner, chunk);
		}
		if (match !== owner) {
			chunk.dependents.push(match);
		}
	}

	return [...chunks.values()];
}
